import React, { useMemo, useCallback } from "react";
import { Box, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import AuthPageLayout from "../../../components/layout/AuthPageLayout";
import { Form } from "../../../components/Form";
import FormTextInput from "../../../components/Form/FormTextInput";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import {
  useFormContext,
  FormDataType,
} from "../../../components/Form/FormContext";
import { selectAuthLoading } from "../../../store/selectors/authSelectors";
import { forgotPassword } from "../../../store/actions/authActions";
import { ROUTES, COOKIE_KEYS } from "../../../constants";
import { setCookie } from "../../../utils/cookieUtils";
import { useApiErrorHandling } from "../../../hooks";
import { ValidationResult } from "../../../hooks/useFormValidation";
import {
  ForgotPasswordContainer,
  ForgotPasswordTitle,
  ForgotPasswordSubtitle,
  ForgotPasswordForm as ForgotPasswordFormStyled,
  ForgotPasswordActions,
} from "./ForgotPasswordPage.styled";

interface ForgotPasswordFormProps {
  apiErrors: string[];
  fieldErrors: { [k: string]: string[] };
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  apiErrors,
  fieldErrors,
}) => {
  const { t } = useTranslation();
  const isLoading = useSelector(selectAuthLoading);
  const { validationResult } = useFormContext();

  // Disable submit when client-side validation reports errors
  const isFormValid =
    !!validationResult?.isValid &&
    Array.isArray(validationResult?.errorMessages) &&
    validationResult.errorMessages.length === 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "44px" }}>
      <FormTextInput
        name="email"
        label={t("forgotPassword.email")}
        type="email"
        placeholder={t("forgotPassword.emailPlaceholder")}
        required
      />

      <FormFieldErrors apiErrors={apiErrors} fieldErrors={fieldErrors} />

      <ForgotPasswordActions>
        <Button variant="outlined" onClick={() => window.history.back()}>
          {t("forgotPassword.goToLogin")}
        </Button>

        <Button
          type="submit"
          variant="contained"
          disabled={!isFormValid || isLoading}
        >
          {isLoading
            ? t("forgotPassword.sending")
            : t("forgotPassword.confirm")}
        </Button>
      </ForgotPasswordActions>
    </Box>
  );
};

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { apiErrors, fieldErrors, clearErrors, handleApiError } =
    useApiErrorHandling({
      defaultErrorMessage: "Failed to send reset email. Please try again.",
    });

  const validationRules = useMemo(
    () => ({
      email: {
        required: true,
        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Please enter a valid email address.",
      },
    }),
    []
  );

  const handleSubmit = useCallback(
    async (formData: FormDataType, validationResult: ValidationResult) => {
      // Block submit strictly based on validationRules outcome
      const hasClientErrors =
        !validationResult?.isValid ||
        (Array.isArray(validationResult?.errorMessages) &&
          validationResult.errorMessages.length > 0);
      if (hasClientErrors) return;

      clearErrors(); // Clear previous errors
      try {
        const email = String(formData.email);
        await dispatch(forgotPassword(email)).unwrap();
        setCookie(COOKIE_KEYS.FORGOT_EMAIL_HINT, email, 0);
        navigate(ROUTES.CHECK_EMAIL);
      } catch (errors: unknown) {
        handleApiError(errors);
      }
    },
    [dispatch, navigate, clearErrors, handleApiError]
  );

  return (
    <AuthPageLayout>
      <ForgotPasswordContainer>
        <ForgotPasswordTitle>{t("forgotPassword.title")}</ForgotPasswordTitle>

        <ForgotPasswordSubtitle>
          {t("forgotPassword.subtitle")}
        </ForgotPasswordSubtitle>

        <ForgotPasswordFormStyled>
          <Form
            validationRules={validationRules}
            onSubmit={handleSubmit}
            showFieldErrors={false}
          >
            <ForgotPasswordForm
              apiErrors={apiErrors}
              fieldErrors={fieldErrors}
            />
          </Form>
        </ForgotPasswordFormStyled>
      </ForgotPasswordContainer>
    </AuthPageLayout>
  );
};

export default ForgotPasswordPage;
